"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowDown01Icon, Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import * as React from "react";
import { ResponsivePopover } from "../../ui/responsive-popover";
import { Typography } from "../../ui/typography";
import { Checkbox } from "@/components/ui/checkbox";

import { AccommodationType } from "@/types/accommodationType";
import { useAccommodationTypes } from "@/lib/react-query/queries/references/use-accommodation-types";

interface AccommodationTypeFilterProps {
  className?: string;
  selectedTypes?: string[];
  onSelect?: (types: string[] | undefined) => void;
}

export function AccommodationTypeFilter({
  className,
  selectedTypes,
  onSelect,
}: AccommodationTypeFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<string[]>(selectedTypes || []);

  // Get dynamic accommodation types data
  const {
    data: accommodationTypesResponse,
    isLoading,
    error,
  } = useAccommodationTypes();

  // Extract accommodation types array from the API response
  const accommodationTypes: AccommodationType[] =
    accommodationTypesResponse?.data || [];

  // Convert API data to the format expected by the component
  const ACCOMMODATION_OPTIONS = React.useMemo(() => {
    return accommodationTypes.map((type) => ({
      label: type.title,
      // /references/types returns PascalCase slugs ("Villa"), the search
      // endpoint matches on lowercase ("villa")
      value: type.slug.toLowerCase(),
      id: type.id,
      thumbnail: type.thumbnail,
      // TODO: Replace with real count from API
      count: 0,
    }));
  }, [accommodationTypes]);

  React.useEffect(() => {
    setSelected(selectedTypes || []);
  }, [selectedTypes]);

  const handleCheckboxChange = (value: string) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleApply = () => {
    onSelect?.(selected.length > 0 ? selected : undefined);
    setOpen(false);
  };

  const handleDelete = () => {
    setSelected([]);
    onSelect?.(undefined);
  };

  // Show selected count or default label
  const MAX_LABEL_LENGTH = 16; // adjust as needed
  const selectedLabels = ACCOMMODATION_OPTIONS.filter((opt) =>
    selected.includes(opt.value)
  ).map((opt) => opt.label);

  let buttonLabel: React.ReactNode;
  if (selectedLabels.length === 0) {
    buttonLabel = (
      <Typography variant="captionRegular">نوع اقامتگاه</Typography>
    );
  } else {
    const joined = selectedLabels.join("، ");
    let display = joined;
    let collapsed = false;
    if (joined.length > MAX_LABEL_LENGTH) {
      let acc = "";
      const shown: string[] = [];
      for (let i = 0; i < selectedLabels.length; i++) {
        if (
          (acc + (acc ? "، " : "") + selectedLabels[i]).length >
          MAX_LABEL_LENGTH
        ) {
          collapsed = true;
          break;
        }
        if (acc) acc += "، ";
        acc += selectedLabels[i];
        shown.push(selectedLabels[i]);
      }
      display = shown.join("، ") + " ...";
    }
    buttonLabel = display;
  }

  // Show loading state
  if (isLoading) {
    return (
      <Button
        variant="pill"
        className={cn("duration-500 font-normal !text-xs", className)}
        disabled
        type="button"
      >
        <Typography variant="captionRegular">در حال بارگذاری...</Typography>
      </Button>
    );
  }

  // Show error state or empty state
  if (error || accommodationTypes.length === 0) {
    return (
      <Button
        variant="pill"
        className={cn("duration-500 font-normal !text-xs", className)}
        disabled
        type="button"
      >
        <Typography variant="captionRegular">نوع اقامتگاه</Typography>
      </Button>
    );
  }

  return (
    <ResponsivePopover
      open={open}
      onOpenChange={setOpen}
      className="md:w-[424px]"
      direction="bottom"
      sideOffset={16}
      trigger={
        <Button
          id="accommodation-type"
          variant="pill"
          className={cn(
            "duration-500 font-normal !text-xs",
            selected.length ? "border-primary text-primary" : "",
            className
          )}
          type="button"
        >
          {buttonLabel}
          {selected.length ? (
            <span
              tabIndex={0}
              role="button"
              aria-label="حذف فیلترها"
              className="flex items-center justify-center rounded hover:bg-muted focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete();
                }
              }}
            >
              <HugeiconsIcon icon={Cancel01Icon} className="size-[18px]" />
            </span>
          ) : (
            <HugeiconsIcon icon={ArrowDown01Icon} />
          )}
        </Button>
      }
    >
      <Typography
        variant="bodySMedium"
        className="py-4 border-b text-center text-foreground mb-4 md:hidden block"
      >
        نوع اقامتگاه
      </Typography>
      <div className="space-y-3 p-2 md:p-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          {ACCOMMODATION_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 cursor-pointer select-none"
            >
              <Checkbox
                checked={selected.includes(option.value)}
                onCheckedChange={() => handleCheckboxChange(option.value)}
              />
              <span className="flex-1 text-right text-[13px]">
                {option.label}
                <span className="text-xs text-muted-foreground mr-2">
                  ({option.count})
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex justify-between gap-10 items-center mt-6 md:mt-0 md:border-t px-2 md:p-4">
        <Button variant="ghost" onClick={handleDelete} className="p-1 md:p-1">
          <HugeiconsIcon
            className="size-[18px] hidden md:block"
            icon={Cancel01Icon}
          />
          <Typography variant="bodySRegular">حذف</Typography>
        </Button>
        <Button
          onClick={handleApply}
          className="flex-1 md:flex-none h-[50px] md:h-[35px] rounded-lg font-normal"
        >
          <Typography variant="captionMedium" className="md:hidden">
            اعمال تغییرات
          </Typography>
          <Typography
            variant="captionRegular"
            className="hidden md:inline px-1"
          >
            اعمال
          </Typography>
        </Button>
      </div>
    </ResponsivePopover>
  );
}
